import { HttpErrorResponse } from '@angular/common/http';
import { ChangeDetectionStrategy, Component, OnDestroy, OnInit } from '@angular/core';
import { FormControl, FormGroup, Validators } from '@angular/forms';
import { ActivatedRoute, Router } from '@angular/router';
import { combineLatest, ReplaySubject, Subscription } from 'rxjs';
import { map, shareReplay } from 'rxjs/operators';
import { MediaService } from '../../services/media.service';
import { Buyer, BuyerAddress } from '../../models/buyer.model';
import { Address } from './../../models/address.model';
import { Buyer as OrderBuyer } from './../../models/order/buyer.model';
import { Consignee, ConsigneeAddress } from './../../models/order/consignee.model';
import { Delivery } from './../../models/order/delivery.model';
import { BuyersApiService } from './../../services/buyers-api.service';
import { ConsigneesApiService } from './../../services/consignees-api.service';
import { NotificationsService } from './../../services/notifications.service';
import { OrderService } from './../../services/order.service';
import { OrdersApiService } from './../../services/orders-api.service';

@Component({
  selector: 'app-delivery-details',
  templateUrl: './delivery-details.component.html',
  styleUrls: ['./delivery-details.component.scss'],
  changeDetection: ChangeDetectionStrategy.OnPush,
})
export class DeliveryDetailsComponent implements OnInit, OnDestroy {

  private subscriptions = new Map<string, Subscription>();

  public $buyersSelection = new ReplaySubject<BuyerAddress[]>(1);
  public buyersSelection$ = this.$buyersSelection.asObservable();
  public $buyersFilter = new ReplaySubject<string>(1);
  public buyersFilter$ = this.$buyersFilter.asObservable();
  public $buyersSortOrder = new ReplaySubject<number>();
  public buyerSortOrder$ = this.$buyersSortOrder.asObservable();
  public buyers$ = combineLatest([
    this.buyersApiService.buyersByAddress$,
    this.buyerSortOrder$
  ]).pipe(
    map(([buyers, order]) => buyers
      .sort((a: BuyerAddress, b: BuyerAddress) => // TODO: better performance to sort after the pagination?
        a?.name === b?.name
          ? a?.address?.name > b?.address?.name ? order : -order
          : a?.name > b?.name ? order : -order)
    ),
    shareReplay(1)
  );

  public $consigneesSelection = new ReplaySubject<ConsigneeAddress[]>(1);
  public consigneesSelection$ = this.$consigneesSelection.asObservable();
  public $consigneesFilter = new ReplaySubject<string>(1);
  public consigneesFilter$ = this.$consigneesFilter.pipe();
  public $consigneesSortOrder = new ReplaySubject<number>(1);
  public consigneesortOrder$ = this.$consigneesSortOrder.asObservable();
  public consignees$ = combineLatest([
    this.consigneesApiService.consigneesByAddress$,
    this.consigneesortOrder$
  ])
    .pipe(
      map(([consignees, order]) => consignees
        .sort((a: ConsigneeAddress, b: ConsigneeAddress) => // TODO: better performance to sort after the pagination?
          a?.name === b?.name
            ? a?.preference > b?.preference ? order : -order
            : a?.name > b?.name ? order : -order)
      ),
      shareReplay(1)
    );

  consigneesLoading: boolean;
  buyersLoading: boolean;
  deliveryDetailsForm: FormGroup;
  maxDate: Date;
  minDate: Date;
  consignees: Consignee[];
  buyers: Buyer[];

  // things we need to gether to make the final order object
  delivery: Delivery = { methodCode: '', date: '', instructions: '' };
  consignee: Consignee = { id: null, name: '', addressId: null, reference: '' };
  buyer: OrderBuyer = { id: null, name: '', addressId: null, reference: '' };

  saveDeliveryDetails = false;

  constructor(
    public buyersApiService: BuyersApiService,
    public consigneesApiService: ConsigneesApiService,
    public orderService: OrderService,
    public ordersApiService: OrdersApiService,
    public mediaService: MediaService,
    private router: Router,
    private activedRoute: ActivatedRoute,
    private notification: NotificationsService
  ) { }

  ngOnInit(): void {

    this.createForm();

    // when orders are sent, navigate to confirmation
    this.subscriptions.set('ordersApiOrderSent',
      this.ordersApiService.orderSent.subscribe(
        data => {
          console.log('Order POST OK', data);
          if (!this.saveDeliveryDetails) {
            window.localStorage.removeItem('currentOrderDetails');
          }
          this.router.navigate(['..', 'confirmation', data.id || 'unknown'], { relativeTo: this.activedRoute });
        })
    );
    this.subscriptions.set('ordersApiOrderSentError',
      this.ordersApiService.orderSentError.subscribe(
        (error: HttpErrorResponse) => {
          console.error('Order POST failed', error);
          const variables: Record<string, string> = {
            code: (error.error && error.error.code || error.error.status) || error.status || '-1',
            message: (error.error && error.error.message || error.statusText) || '(no error message)'
          };
          this.notification.sendError('NOTIFICATIONS.TEXT_UNABLE_TO_SEND_ORDER', variables);
        })
    );

  }

  ngOnDestroy() {
    this.subscriptions.forEach(subscription => subscription.unsubscribe());
  }

  onDatepickerFilter(date: any): boolean {
    const earliest = new Date(Date.now() + 3600 * 24 * 1 * 1000);
    const latest = new Date(Date.now() + 3600 * 24 * 60 * 1000);
    return date.isoWeekday() < 6 && date.isBetween(earliest, latest);
  }

  // buyer
  onSelectBuyer(buyer: OrderBuyer, address: Address): void {
    this.deliveryDetailsForm.patchValue({
      buyer: {
        id: buyer.id,
        name: buyer.name
      },
      consignee: {
        id: undefined,
        name: undefined
      }
    });
    this.buyersApiService.select(buyer.id);
  }

  // consignee
  onSelectConsignee(consignee: Consignee, address: Address): void {
    this.deliveryDetailsForm.patchValue({
      consignee: {
        id: consignee.id,
        name: consignee.name
      }
    });
  }

  // form helpers
  createForm() {

    // form kinda matches 1 to 1 with the Order model as expected by order service
    // validators for anything related to user input
    // id and name for buyer and consignee is looked up when selecting an address id so we don't really need to validate that
    this.deliveryDetailsForm = new FormGroup({
      buyer: new FormGroup({
        id: new FormControl(''), // 111111,
        name: new FormControl(''), // "Buyer Name",
        addressId: new FormControl('', Validators.required), // 991211,
        reference: new FormControl('', Validators.maxLength(100)), // "Lot 1234"
      }),
      consignee: new FormGroup({
        id: new FormControl(''), // 777777,
        name: new FormControl(''), // "Consignee Two",
        addressId: new FormControl('', Validators.required), // 777111,
        reference: new FormControl('', Validators.maxLength(100)), // "PO6655"
      }),
      delivery: new FormGroup({
        // "Delivery",
        methodCode: new FormControl('', Validators.required),
        // "2018-03-05T00:00:00.000Z",
        date: new FormControl('', Validators.required),
        // "Unloading only on Sundays. Call before unloading +441234123123"
        // Note: decreased maximum length compared to API docs (1000) at request from customer
        instructions: new FormControl('', Validators.maxLength(500)),
      }),
      saveDetails: new FormControl(false)
    });

    // load form data from local storage
    try {
      const previousDetails = window.localStorage.getItem('currentOrderDetails');
      if (previousDetails) {
        const details = JSON.parse(previousDetails);
        this.buyersApiService.select(details?.buyer?.id);
        this.deliveryDetailsForm.patchValue(details);
        this.orderService.updateOrder(this.deliveryDetailsForm.value);
        this.saveDeliveryDetails = details.saveDetails;
      }
    } catch (e) {
      console.error('Restoring delivery details caused an exception:', e);
    }

    // when form is updated
    this.subscriptions.set('deliveryDetailsFormChanges',
      this.deliveryDetailsForm.valueChanges.subscribe(data => {
        this.orderService.updateOrder(this.deliveryDetailsForm.value);
        // store in local storage
        this.saveDeliveryDetails = data.saveDetails;
        window.localStorage.setItem('currentOrderDetails', JSON.stringify(data));
      })
    );
  }

  // submit
  onSubmitOrder() {
    this.orderService.submit();
  }

}
